/** クイックオープン (Cmd/Ctrl+P) の検索結果リスト構築ロジック。 */

import type { EnvironmentFileDto } from "@/lib/api";
import { isHiddenEnvironmentFile } from "@/lib/environment-files";
import { fuzzyMatch, toHighlightRuns, type HighlightRun } from "@/lib/fuzzy";

export const QUICK_OPEN_MAX_RESULTS = 50;

/** 入力が止まってからサーバー検索を投げるまでの待ち時間。 */
export const QUICK_OPEN_SEARCH_DEBOUNCE_MS = 150;

export interface QuickOpenSearchHandlers {
	/** クエリに対応する候補を取得する。空クエリは「開いているタブ」の解決に使う。 */
	fetchCandidates: (query: string) => Promise<EnvironmentFileDto[]>;
	onCandidates: (candidates: EnvironmentFileDto[]) => void;
	onError: (error: unknown) => void;
}

export interface QuickOpenItem {
	fileId: string;
	path: string;
	/** basename (最後の "/" 以降)。 */
	name: string;
	/** 親ディレクトリ ("" = ルート直下)。 */
	dir: string;
	nameRuns: HighlightRun[];
	dirRuns: HighlightRun[];
}

function toQuickOpenItem(file: EnvironmentFileDto, indices: readonly number[]): QuickOpenItem {
	const basenameStart = file.path.lastIndexOf("/") + 1;
	const dir = file.path.slice(0, Math.max(basenameStart - 1, 0));
	const name = file.path.slice(basenameStart);
	const dirIndices = indices.filter((i) => i < dir.length);
	const nameIndices = indices.filter((i) => i >= basenameStart).map((i) => i - basenameStart);
	return {
		fileId: file.id,
		path: file.path,
		name,
		dir,
		nameRuns: toHighlightRuns(name, nameIndices),
		dirRuns: toHighlightRuns(dir, dirIndices),
	};
}

/**
 * クエリが空のときの並び順: アクティブなタブ → 開いている他のタブ (タブ順) → 残りをパス辞書順。
 * 「最近開いた順」の厳密なデータは無いため、開いているタブの並びで近似する。
 */
function orderByRecency(
	candidates: readonly EnvironmentFileDto[],
	openFileIds: readonly string[],
	activeFileId: string | null,
): EnvironmentFileDto[] {
	const byId = new Map(candidates.map((f) => [f.id, f] as const));
	const used = new Set<string>();
	const ordered: EnvironmentFileDto[] = [];

	if (activeFileId !== null) {
		const active = byId.get(activeFileId);
		if (active) {
			ordered.push(active);
			used.add(active.id);
		}
	}
	for (const id of openFileIds) {
		if (used.has(id)) continue;
		const file = byId.get(id);
		if (!file) continue;
		ordered.push(file);
		used.add(file.id);
	}
	const rest = [...candidates]
		.filter((f) => !used.has(f.id))
		.sort((a, b) => a.path.localeCompare(b.path));
	return [...ordered, ...rest];
}

/**
 * 環境ファイル一覧・タブ状態・入力クエリから、クイックオープンの表示項目を構築する。
 * ディレクトリと隠しファイル (`.session_cwd` 等) は除外し、最大 {@link QUICK_OPEN_MAX_RESULTS} 件を返す。
 */
export function buildQuickOpenItems(
	files: readonly EnvironmentFileDto[],
	query: string,
	openFileIds: readonly string[],
	activeFileId: string | null,
): QuickOpenItem[] {
	const candidates = files.filter((f) => !f.isDirectory && !isHiddenEnvironmentFile(f.path));
	const trimmed = query.trim();

	if (trimmed === "") {
		return orderByRecency(candidates, openFileIds, activeFileId)
			.slice(0, QUICK_OPEN_MAX_RESULTS)
			.map((file) => toQuickOpenItem(file, []));
	}

	const scored = candidates.flatMap((file) => {
		const match = fuzzyMatch(trimmed, file.path);
		return match ? [{ file, indices: match.indices, score: match.score }] : [];
	});
	scored.sort((a, b) => {
		if (b.score !== a.score) return b.score - a.score;
		if (a.file.path.length !== b.file.path.length) return a.file.path.length - b.file.path.length;
		return a.file.path.localeCompare(b.file.path);
	});

	return scored
		.slice(0, QUICK_OPEN_MAX_RESULTS)
		.map(({ file, indices }) => toQuickOpenItem(file, indices));
}

/**
 * クイックオープンの候補取得を、入力のデバウンスと古いレスポンスの破棄付きで実行する。
 * 表示用の採点は取得結果に対して {@link buildQuickOpenItems} が行うため、ここでは扱わない。
 */
export class QuickOpenSearch {
	private readonly handlers: QuickOpenSearchHandlers;
	private timer: ReturnType<typeof setTimeout> | null = null;
	private latestRequest = 0;
	private disposed = false;

	constructor(handlers: QuickOpenSearchHandlers) {
		this.handlers = handlers;
	}

	/** 空クエリ (パレットを開いた直後の初期表示) は待たずに取得し、入力中だけデバウンスする。 */
	setQuery(query: string): void {
		if (this.disposed) return;
		this.clearTimer();
		const request = ++this.latestRequest;
		const run = () => {
			this.timer = null;
			void this.fetch(query, request);
		};
		if (query.trim() === "") run();
		else this.timer = setTimeout(run, QUICK_OPEN_SEARCH_DEBOUNCE_MS);
	}

	private async fetch(query: string, request: number): Promise<void> {
		try {
			const candidates = await this.handlers.fetchCandidates(query);
			if (this.isStale(request)) return;
			this.handlers.onCandidates(candidates);
		} catch (error) {
			if (this.isStale(request)) return;
			this.handlers.onError(error);
		}
	}

	private isStale(request: number): boolean {
		return this.disposed || request !== this.latestRequest;
	}

	dispose(): void {
		this.disposed = true;
		this.clearTimer();
	}

	private clearTimer(): void {
		if (this.timer !== null) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}
}
